import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

import "@fontsource/poppins";
import '../style.css';
import 'bootswatch/dist/lux/bootstrap.min.css';
import 'filepond/dist/filepond.min.css';

import VehicleData from './VehicleData';
import ValuationCard from './ValuationCard';
import FormPage1 from './FormPage1';
import FormPage2 from './FormPage2';
import FormPage3 from './FormPage3';
import FormPage4 from './FormPage4';

import { DEBUG } from '../index';
import { Modal, Button, Collapse } from 'react-bootstrap';
import { useWidget } from '../controllers/MainViewController';
import { getWidgetConfig } from '../util/api';
import { buildPagesFlow, Logo } from '../util/aux';

const MainView = (props) => {
  const [featureFlags, setFeatureFlags] = useState(null);
  const { widgetId } = useParams();
  
  // Basic default value just to initialize safely
  const defaultPages = ['Contact', 'Car Info', 'Overview', 'Details', 'Valuation', 'Done!'];
  
  const initValue = {
    currentPage: 'Contact',
    pageNumber: 1,
    currentPageIndex: 0,
    pages: defaultPages,
    widgetId,
    estimated_arrival: "immediate",
    min_price: 0,
    featureFlags: {}
  };
  
  const { state, setState, nextPage, prevPage } = useWidget(initValue);

  // Get the current page name safely
  const currentPage = state.pages && state.pages.length > state.currentPageIndex
    ? state.pages[state.currentPageIndex]
    : '';
  
  useEffect(() => {
    async function fetchFeatureFlags() {
      if (!widgetId) return;
      try {
        const { widgetData, error } = await getWidgetConfig(widgetId);
        if (error) {
          console.error("[!] Error fetching widget config:", error);
          return;
        }
        if (widgetData) {
          setFeatureFlags(widgetData);
        }
      } catch (error) {
        console.error("[!] Unexpected error fetching widget config:", error);
      }
    }
  
    fetchFeatureFlags();
  }, [widgetId]);
  

  // 📦 Update the pages in state once featureFlags are available
  useEffect(() => {
    // Avoid running if featureFlags haven't been fetched yet
    if (!featureFlags) return;

    // Generate the appropriate list of pages based on the flags
    const updatedPages = buildPagesFlow(featureFlags);

    // Update the widget state with the new pages and flags
    setState((prev) => ({
      ...prev,
      pages: updatedPages,
      featureFlags,
    }));
    
  }, [featureFlags, setState]);

  
  // 💡 Skip rendering until flags are loaded
  if (!featureFlags) {
    return null;
  }
  
  let fragment;
  let styling;

  if (!widgetId) {
    fragment = (
      <div className="center-flex">
        <h2>Something went wrong</h2>
      </div>
    );
    styling = 'p2-style modal-fullscreen-sm-down';
  } else {
    switch (currentPage) {
      case 'Contact':
        fragment = (
          <FormPage1
            id='form-page-1'
            state={state}
            setState={setState}
            nextPage={nextPage}
            prevPage={prevPage}
          />
        );
        styling = 'p2-style modal-fullscreen-sm-down';
        break;
      case 'Car Info':
        fragment = (
          <FormPage2
            id='form-page-1'
            state={state}
            setState={setState}
            nextPage={nextPage}
            prevPage={prevPage}
          />
        );
        styling = 'p2-style modal-fullscreen-sm-down';
        break;
      case 'Overview':
        fragment = (
          <VehicleData
            state={state}
            setState={setState}
            nextPage={nextPage}
            prevPage={prevPage}
          />
        );
        styling = 'p2-style modal-fullscreen-sm-down';
        break;
      case 'Details':
        if (!featureFlags.hideDetails) {
        fragment = (
          <FormPage3
            id='form-page-2'
            state={state}
            setState={setState}
            nextPage={nextPage}
            prevPage={prevPage}
          />
        );
        styling = 'p2-style modal-fullscreen-sm-down';
        break;
      }
      case 'Valuation':
        if (!featureFlags.hideValuation) {
          fragment = (
            <ValuationCard
              state={state}
              setState={setState}
              nextPage={nextPage}
              prevPage={prevPage}
            />
          );
          styling = 'p2-style modal-fullscreen-sm-down';
          break;
        }
      case 'Done!':  
        if (!featureFlags.hideDone) {
          fragment = (
            <FormPage4
              state={state}
              setState={setState}
              nextPage={nextPage}
              prevPage={prevPage}
            />
          );
        }
    }
  }

  // Determine if we're on the last page
  const isLastPage = currentPage === 'Done!';

  return (
    <>
      {/* Logo in center of the full screen */}
      {featureFlags.logoImageName && (
        <Logo 
          src={`/assets/logos/${featureFlags.logoImageName}`} 
          align={featureFlags.logoImageAlign}
          size={featureFlags.logoImageSize}
        />
      )}

      {/* Modal content */}
      {!isLastPage ? (
        <Modal show={true} backdrop={false} dialogClassName={styling}>
          {fragment}
          {DEBUG ? (
            <pre>
              <DebugOutput debug={JSON.stringify(state, null, '  ')} />
            </pre>
          ) : null}
        </Modal>
      ) : (
        <>{fragment}</>
      )}
    </>
  );

};

function DebugOutput(props) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        onClick={() => setOpen(!open)}
        aria-controls="debug-collapsible"
        aria-expanded={open}
      >
        Debug
      </Button>
      <Collapse in={open}>
        <div id="debug-collapsible">{props.debug}</div>
      </Collapse>
    </>
  );
}

export default MainView;
